// ============================================================
// SevenStars SmartCare Passport — Production Data Store
// Backend: Supabase (PostgreSQL)
// ============================================================
import { supabase } from './supabase';
import { REAL_CUSTOMERS, REAL_VEHICLES } from './realdata';

// ── Token helpers (deterministic — same inputs = same token) ─
function generateUnitToken(passportId, vehicleId) {
  const payload = { pid: passportId, vid: vehicleId };
  return btoa(JSON.stringify(payload))
    .replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}

export function decodeUnitToken(token) {
  try {
    const padded = token.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(padded));
  } catch { return null; }
}

// ── PIN helpers ──────────────────────────────────────────────
// TODO (V3.2): Move to bcrypt on Supabase Edge Function
function hashPin(pin) {
  let h = 0;
  for (let i = 0; i < pin.length; i++) {
    h = ((h << 5) - h) + pin.charCodeAt(i);
    h |= 0;
  }
  return 'PIN_' + Math.abs(h).toString(36) + '_' + pin.length;
}
function verifyPin(pin, hash) { return hashPin(pin) === hash; }

function uid() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 6);
}

// ── Seed check — only seed once ─────────────────────────────
let seedInProgress = false;

export async function seedDemoData() {
  if (seedInProgress) return;
  seedInProgress = true;
  try {
    // Check if already seeded
    const { count } = await supabase
      .from('passports')
      .select('*', { count: 'exact', head: true });

    if (count > 0) {
      console.log(`[Supabase] Already seeded: ${count} passports`);
      return;
    }

    console.log('[Supabase] Seeding 208 customers...');
    const BATCH = 50;

    // Insert customers in batches
    for (let i = 0; i < REAL_CUSTOMERS.length; i += BATCH) {
      const batch = REAL_CUSTOMERS.slice(i, i + BATCH).map(c => ({
        id: c.id, name: c.name, mobile: c.mobile || '',
        email: c.email || '', municipality: c.municipality || '',
        created_at: c.createdAt || '',
      }));
      const { error } = await supabase.from('customers').upsert(batch, { onConflict: 'id' });
      if (error) console.error('Customer seed error:', error.message);
    }

    // Insert vehicles in batches
    for (let i = 0; i < REAL_VEHICLES.length; i += BATCH) {
      const batch = REAL_VEHICLES.slice(i, i + BATCH).map(v => ({
        id: v.id, customer_id: v.customerId, model: v.model,
        brand: v.brand || 'SEVENSTARS', serial_number: v.serialNumber || '',
        purchase_date: v.purchaseDate || '', warranty_months: v.warrantyMonths || 3,
        battery_type: v.batteryType || 'Lead Acid', color: v.color || '',
        warranty_status: v.warrantyStatus || 'active',
      }));
      const { error } = await supabase.from('vehicles').upsert(batch, { onConflict: 'id' });
      if (error) console.error('Vehicle seed error:', error.message);
    }

    // Generate passports for all vehicles
    const passports = REAL_VEHICLES.map((v, i) => {
      const year = (v.purchaseDate || '2024-10-17').substring(0, 4);
      const passportId = `SSP-${year}-${String(i + 1).padStart(6, '0')}`;
      const token = generateUnitToken(passportId, v.id);
      return {
        id: uid(),
        passport_id: passportId,
        unit_id: `SS-${v.model.replace(/\s+/g,'').toUpperCase()}-${year}-${String(i+1).padStart(3,'0')}`,
        warranty_id: `WRN-${year}-${String(i + 1).padStart(6, '0')}`,
        vehicle_id: v.id,
        customer_id: v.customerId,
        token,
        qr_url: `?unit=${token}`,
        is_activated: false,
        pin_hash: null,
        activated_at: null,
        created_at: v.purchaseDate || '',
      };
    });

    for (let i = 0; i < passports.length; i += BATCH) {
      const { error } = await supabase.from('passports')
        .upsert(passports.slice(i, i + BATCH), { onConflict: 'token' });
      if (error) console.error('Passport seed error:', error.message);
    }

    // Seed service record for first customer
    await supabase.from('service_records').upsert([{
      id: 'sr1', vehicle_id: REAL_VEHICLES[0]?.id,
      customer_id: REAL_CUSTOMERS[0]?.id,
      date: '2026-01-15', type: 'Regular Check-up',
      technician_name: 'Ronnie Bautista',
      technician_notes: 'Brake pads adjusted, battery checked — all good.',
      parts_replaced: 'None', next_service_date: '2026-04-15',
    }], { onConflict: 'id' });

    console.log('[Supabase] Seeding complete!');
  } finally {
    seedInProgress = false;
  }
}

// ============================================================
// PASSPORT STORE
// ============================================================
export const passportStore = {
  getAll: async () => {
    const { data } = await supabase.from('passports').select('*').order('created_at');
    return data || [];
  },

  getByToken: async (token) => {
    const { data } = await supabase.from('passports')
      .select('*').eq('token', token).single();
    return data;
  },

  getByVehicle: async (vehicleId) => {
    const { data } = await supabase.from('passports')
      .select('*').eq('vehicle_id', vehicleId).single();
    return data;
  },

  getByCustomer: async (customerId) => {
    const { data } = await supabase.from('passports')
      .select('*').eq('customer_id', customerId);
    return data || [];
  },

  activate: async (token, pinHash) => {
    const { error } = await supabase.from('passports')
      .update({ is_activated: true, pin_hash: pinHash, activated_at: new Date().toISOString() })
      .eq('token', token);
    if (error) throw error;
    await supabase.from('audit_logs').insert([{
      id: uid(), event_type: 'passport.activated', token,
    }]);
  },

  verifyPin: (passport, pin) => {
    if (!passport?.pin_hash) return false;
    return verifyPin(pin, passport.pin_hash);
  },

  changePin: async (token, newPin) => {
    await supabase.from('passports')
      .update({ pin_hash: hashPin(newPin), temp_pin_hash: null, temp_pin_expires_at: null })
      .eq('token', token);
  },

  // Generate a temporary PIN (admin use) — valid for 24 hours
  generateTempPin: async (token) => {
    const tempPin = String(Math.floor(100000 + Math.random() * 900000));
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    await supabase.from('passports').update({
      temp_pin_hash: hashPin(tempPin),
      temp_pin_expires_at: expiresAt,
    }).eq('token', token);
    await supabase.from('audit_logs').insert([{
      id: uid(), event_type: 'passport.temp_pin_generated', token,
    }]);
    return tempPin; // Show this to admin ONCE
  },

  // Check PIN — accepts real PIN or valid temp PIN
  checkPin: (passport, pin) => {
    if (!passport) return false;
    if (passport.pin_hash && verifyPin(pin, passport.pin_hash)) return true;
    if (passport.temp_pin_hash && passport.temp_pin_expires_at) {
      const notExpired = new Date(passport.temp_pin_expires_at) > new Date();
      if (notExpired && verifyPin(pin, passport.temp_pin_hash)) return true;
    }
    return false;
  },

  // Reset PIN — clears both PIN and temp PIN, forces re-activation
  resetPin: async (token) => {
    await supabase.from('passports').update({
      pin_hash: null,
      temp_pin_hash: null,
      temp_pin_expires_at: null,
      is_activated: false,
      activated_at: null,
    }).eq('token', token);
    await supabase.from('audit_logs').insert([{
      id: uid(), event_type: 'passport.pin_reset', token,
    }]);
  },

  register: async (vehicleId, customerId) => {
    const { data: existing } = await supabase.from('passports').select('id');
    const count = (existing || []).length;
    const year = new Date().getFullYear();
    const passportId = `SSP-${year}-${String(count + 1).padStart(6, '0')}`;
    const token = generateUnitToken(passportId, vehicleId);
    const item = {
      id: uid(), passport_id: passportId,
      unit_id: `SS-UNIT-${year}-${String(count + 1).padStart(3, '0')}`,
      warranty_id: `WRN-${year}-${String(count + 1).padStart(6, '0')}`,
      vehicle_id: vehicleId, customer_id: customerId,
      token, qr_url: `?unit=${token}`,
      is_activated: false, pin_hash: null, activated_at: null,
      created_at: new Date().toISOString().split('T')[0],
    };
    const { data, error } = await supabase.from('passports').insert([item]).select().single();
    if (error) throw error;
    return data;
  },

  transferOwnership: async (token, newCustomerId, newPinHash) => {
    const { data: current } = await supabase.from('passports')
      .select('customer_id').eq('token', token).single();
    await supabase.from('passports').update({
      previous_customer_id: current?.customer_id,
      customer_id: newCustomerId,
      pin_hash: newPinHash,
      is_activated: true,
      activated_at: new Date().toISOString(),
      transferred_at: new Date().toISOString(),
    }).eq('token', token);
    await supabase.from('audit_logs').insert([{
      id: uid(), event_type: 'passport.transferred', token,
      metadata: { new_customer_id: newCustomerId },
    }]);
  },

  hashPin,
  generateOtp: () => String(Math.floor(100000 + Math.random() * 900000)),
  decodeUnitToken,
};

// ============================================================
// CUSTOMER STORE
// ============================================================
export const customerStore = {
  getAll: async () => {
    const { data } = await supabase.from('customers').select('*').order('name');
    return data || [];
  },

  getById: async (id) => {
    const { data } = await supabase.from('customers').select('*').eq('id', id).single();
    return data;
  },

  searchAdmin: async (q) => {
    const { data } = await supabase.from('customers').select('*')
      .ilike('name', `%${q}%`).limit(10);
    return data || [];
  },

  add: async (customerData) => {
    const item = { id: uid(), created_at: new Date().toISOString().split('T')[0], ...customerData };
    const { data, error } = await supabase.from('customers').insert([item]).select().single();
    if (error) throw error;
    return data;
  },

  update: async (id, data) => {
    await supabase.from('customers').update(data).eq('id', id);
  },
};

// ============================================================
// VEHICLE STORE
// ============================================================
export const vehicleStore = {
  getAll: async () => {
    const { data } = await supabase.from('vehicles').select('*');
    return data || [];
  },

  getById: async (id) => {
    const { data } = await supabase.from('vehicles').select('*').eq('id', id).single();
    return data;
  },

  getByCustomer: async (customerId) => {
    const { data } = await supabase.from('vehicles').select('*').eq('customer_id', customerId);
    return data || [];
  },

  add: async (vehicleData) => {
    const item = { id: uid(), ...vehicleData };
    const { data, error } = await supabase.from('vehicles').insert([item]).select().single();
    if (error) throw error;
    return data;
  },

  update: async (id, data) => {
    await supabase.from('vehicles').update(data).eq('id', id);
  },

  getWarrantyInfo: (vehicle) => {
    if (!vehicle?.purchase_date || !vehicle?.warranty_months) return { status: 'unknown', endDate: null, daysLeft: null };
    const end = new Date(vehicle.purchase_date);
    end.setMonth(end.getMonth() + Number(vehicle.warranty_months));
    const now = new Date();
    const daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    let status = daysLeft < 0 ? 'expired' : daysLeft < 30 ? 'expiring' : 'active';
    return { status, endDate: end.toISOString().split('T')[0], daysLeft };
  },
};

// ============================================================
// SERVICE STORES
// ============================================================
export const serviceRecordStore = {
  getAll: async () => {
    const { data } = await supabase.from('service_records').select('*').order('date', { ascending: false });
    return data || [];
  },
  getByVehicle: async (vehicleId) => {
    const { data } = await supabase.from('service_records').select('*')
      .eq('vehicle_id', vehicleId).order('date', { ascending: false });
    return data || [];
  },
  add: async (recordData) => {
    const item = { id: uid(), ...recordData };
    const { data, error } = await supabase.from('service_records').insert([item]).select().single();
    if (error) throw error;
    return data;
  },
};

export const serviceRequestStore = {
  getAll: async () => {
    const { data } = await supabase.from('service_requests').select('*').order('submitted_at', { ascending: false });
    return data || [];
  },
  getByVehicle: async (vehicleId) => {
    const { data } = await supabase.from('service_requests').select('*')
      .eq('vehicle_id', vehicleId).order('submitted_at', { ascending: false });
    return data || [];
  },
  getByCustomer: async (customerId) => {
    const { data } = await supabase.from('service_requests').select('*')
      .eq('customer_id', customerId).order('submitted_at', { ascending: false });
    return data || [];
  },
  add: async (requestData) => {
    const item = {
      id: uid(),
      status: 'pending',
      submitted_at: new Date().toISOString().split('T')[0],
      ...requestData,
    };
    const { data, error } = await supabase.from('service_requests').insert([item]).select().single();
    if (error) throw error;
    return data;
  },
  updateStatus: async (id, status) => {
    await supabase.from('service_requests').update({ status }).eq('id', id);
  },
};

export const referralStore = {
  getAll: async () => {
    const { data } = await supabase.from('referrals').select('*').order('submitted_at', { ascending: false });
    return data || [];
  },
  getByReferrer: async (customerId) => {
    const { data } = await supabase.from('referrals').select('*')
      .eq('referrer_id', customerId);
    return data || [];
  },
  add: async (referralData) => {
    const item = {
      id: uid(),
      status: 'pending',
      submitted_at: new Date().toISOString().split('T')[0],
      ...referralData,
    };
    const { data, error } = await supabase.from('referrals').insert([item]).select().single();
    if (error) throw error;
    return data;
  },
  updateStatus: async (id, status) => {
    await supabase.from('referrals').update({ status }).eq('id', id);
  },
};

export const promotionStore = {
  getAll: async () => {
    const { data } = await supabase.from('promotions').select('*');
    return data || [];
  },
  getActive: async () => {
    const today = new Date().toISOString().split('T')[0];
    const { data } = await supabase.from('promotions').select('*')
      .eq('active', true).gte('valid_until', today);
    return data || [];
  },
  add: async (promoData) => {
    const item = { id: uid(), active: true, ...promoData };
    const { data, error } = await supabase.from('promotions').insert([item]).select().single();
    if (error) throw error;
    return data;
  },
  update: async (id, data) => {
    await supabase.from('promotions').update(data).eq('id', id);
  },
  delete: async (id) => {
    await supabase.from('promotions').delete().eq('id', id);
  },
};

export const transferCodeStore = {
  generate: async (token, currentCustomerId) => {
    const code = Math.random().toString(36).substr(2, 4).toUpperCase() +
                 Math.random().toString(36).substr(2, 4).toUpperCase();
    const item = {
      id: uid(), code, token, current_customer_id: currentCustomerId,
      expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      status: 'pending',
    };
    const { data, error } = await supabase.from('transfer_codes').insert([item]).select().single();
    if (error) throw error;
    return data;
  },

  verify: async (code) => {
    const { data } = await supabase.from('transfer_codes')
      .select('*').eq('code', code).eq('status', 'pending').single();
    if (!data) return null;
    if (new Date(data.expires_at) < new Date()) return null;
    return data;
  },

  markUsed: async (code, newCustomerId) => {
    await supabase.from('transfer_codes').update({
      status: 'used',
      used_at: new Date().toISOString(),
      new_customer_id: newCustomerId,
    }).eq('code', code);
  },
};

export const auditStore = {
  getAll: async () => {
    const { data } = await supabase.from('audit_logs').select('*').order('created_at', { ascending: false }).limit(100);
    return data || [];
  },
};
